import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBannerDetails, clearSelectedBanner, deleteBanner } from '../../../RTK/categories/BannerSlice';
import { X, Loader2, Calendar, RefreshCcw, Pencil, Trash2 } from 'lucide-react';
import EditBannerPopup from './EditBannerPopup';
import ConfirmDialog from './ConfirmDialog';

const BannerPopup = ({ isVisible, bannerId, onClose }) => {
  const dispatch = useDispatch();
  const { selectedBanner, isLoadingDetails, detailsError } = useSelector((state) => state.banners);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isVisible && bannerId) {
      dispatch(fetchBannerDetails(bannerId));
    }
    return () => {
      dispatch(clearSelectedBanner());
    };
  }, [bannerId, isVisible, dispatch]);

  const handleEdit = () => {
    setShowEditPopup(true);
  };

  const handleCloseEdit = () => {
    setShowEditPopup(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    const result = await dispatch(deleteBanner(bannerId));
    if (!result.error) {
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" aria-modal="true" role="dialog">
        <div className="relative bg-gray-900 rounded-2xl w-full max-w-2xl overflow-hidden border border-gray-800">
          <button
            onClick={onClose}
            className="absolute top-4 left-4 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>

          {isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center h-96 gap-3">
              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
              <span className="text-gray-400">جاري التحميل...</span>
            </div>
          ) : detailsError ? (
            <div className="flex flex-col items-center justify-center h-96 gap-4">
              <p className="text-red-500 text-lg" role="alert">{detailsError}</p>
              <button
                onClick={() => dispatch(fetchBannerDetails(bannerId))}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <RefreshCcw className="w-4 h-4" />
                <span>إعادة المحاولة</span>
              </button>
            </div>
          ) : selectedBanner && (
            <div className="h-full" dir="rtl">
              {selectedBanner.image && (
                <div
                  className="w-full h-64 bg-cover bg-center"
                  style={{ backgroundImage: `url(${selectedBanner.image})` }}
                  role="img"
                  aria-label={selectedBanner.title}
                />
              )}
              <div className="p-6">
                <h2 className="text-2xl font-bold text-white mb-4">{selectedBanner.title}</h2>
                {selectedBanner.description && (
                  <p className="text-gray-300 mb-6">{selectedBanner.description}</p>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-400 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">تاريخ الإنشاء</span>
                    </div>
                    <p className="text-white">
                      {new Date(selectedBanner.created_at).toLocaleDateString('ar-EG')}
                    </p>
                  </div>

                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-400 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">آخر تحديث</span>
                    </div>
                    <p className="text-white">
                      {new Date(selectedBanner.updated_at).toLocaleDateString('ar-EG')}
                    </p>
                  </div>

                  <div className="col-span-2 bg-gray-800/50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">الحالة</span>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        selectedBanner.is_active 
                          ? 'bg-green-500/20 text-green-500' 
                          : 'bg-red-500/20 text-red-500'
                      }`}>
                        {selectedBanner.is_active ? 'مفعل' : 'غير مفعل'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 mt-6">
                  
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    <span>تعديل</span>
                  </button>

                  <button
                    onClick={handleDeleteClick}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>حذف</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEditPopup && (
        <EditBannerPopup
          isVisible={showEditPopup}
          banner={selectedBanner}
          onClose={handleCloseEdit}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        message="هل أنت متأكد من حذف هذا البانر؟"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default BannerPopup;